// src/components/TaskCard.tsx
import { useState } from 'react'
import { Check, RotateCcw, Trash2 } from 'lucide-react'
import { useTasks } from '../stores/useTasks'
import type { Task } from '../types'

function fmtMins(mins: number): string {
  if (mins < 60) return `${mins}m`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m === 0 ? `${h}h` : `${h}h${m}m`
}

export function TaskCard({ task }: { task: Task }) {
  const { classify, markDone, delete: del, setEstimate } = useTasks()
  const [expanded, setExpanded] = useState(false)
  const [editingEst, setEditingEst] = useState(false)
  const [estText, setEstText] = useState(task.estimated_mins ? fmtMins(task.estimated_mins) : '')

  const typeClass = task.type === 'signal' ? 'signal' : task.type === 'noise' ? 'noise' : ''

  function handleEstSubmit() {
    const s = estText.trim().toLowerCase()
    let mins: number | null = null
    const hm = s.match(/^(\d+)\s*h\s*(\d+)\s*m$/)
    if (hm) mins = parseInt(hm[1]) * 60 + parseInt(hm[2])
    const h = s.match(/^(\d+)\s*h$/)
    if (h) mins = parseInt(h[1]) * 60
    const m = s.match(/^(\d+)\s*m$/)
    if (m) mins = parseInt(m[1])
    if (!isNaN(parseInt(s)) && parseInt(s) > 0) mins = parseInt(s)
    setEstimate(task.id, mins)
    setEditingEst(false)
  }

  return (
    <div className={`task-card ${typeClass} ${task.done ? 'done' : ''}`}>
      <div className="task-card-inner" onClick={() => setExpanded(e => !e)}>
        {/* Text + type badge */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sm)', marginBottom: 'var(--sm)' }}>
          <p className={`task-card-text ${task.done ? 'done-text' : ''}`} style={{ flex: 1 }}>
            {task.text}
          </p>
          {task.type !== 'unclassified' && (
            <span className={`badge badge-${task.type === 'signal' ? 'signal' : 'noise'}`}>
              {task.type === 'signal' ? '◈' : '◉'} {task.type}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="task-card-actions">
          {task.type !== 'signal' && (
            <button
              className="badge badge-signal"
              style={{ cursor: 'pointer', border: 'none' }}
              onClick={(e) => { e.stopPropagation(); classify(task.id, 'signal', 'User manual') }}
            >
              ◈ Signal
            </button>
          )}
          {task.type !== 'noise' && (
            <button
              className="badge badge-noise"
              style={{ cursor: 'pointer', border: 'none' }}
              onClick={(e) => { e.stopPropagation(); classify(task.id, 'noise', 'User manual') }}
            >
              ◉ Noise
            </button>
          )}
          <div style={{ flex: 1 }} />

          {/* Estimate badge */}
          <button
            className="badge badge-muted"
            style={{ cursor: 'pointer', border: 'none' }}
            onClick={(e) => { e.stopPropagation(); setEditingEst(true) }}
          >
            {task.estimated_mins ? `~${fmtMins(task.estimated_mins)}` : '+ est'}
          </button>

          {/* Done */}
          <button
            className="btn-icon btn"
            style={{ background: 'transparent', width: 'auto', padding: '4px' }}
            onClick={(e) => { e.stopPropagation(); markDone(task.id, !task.done) }}
          >
            {task.done
              ? <RotateCcw size={15} color="var(--text-dim)" />
              : <Check size={15} color="var(--signal)" />}
          </button>

          {/* Delete */}
          <button
            className="btn-icon btn"
            style={{ background: 'transparent', width: 'auto', padding: '4px' }}
            onClick={(e) => { e.stopPropagation(); del(task.id) }}
          >
            <Trash2 size={15} color="var(--noise)" />
          </button>
        </div>
      </div>

      {/* Expanded: AI reason */}
      {expanded && task.ai_reason && (
        <div className="task-card-reason animate-fade-in">
          "{task.ai_reason}"
        </div>
      )}

      {/* Estimate editor */}
      {editingEst && (
        <div style={{ padding: '0 var(--md) var(--md)', borderTop: '1px solid var(--border)', paddingTop: 'var(--sm)' }} className="animate-fade-in">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sm)' }}>
            <input
              autoFocus
              className="input"
              style={{ flex: 1, padding: 'var(--sm) var(--md)' }}
              value={estText}
              onChange={e => setEstText(e.target.value)}
              placeholder="30m · 1h · 1h30m"
              onKeyDown={e => e.key === 'Enter' && handleEstSubmit()}
              onBlur={handleEstSubmit}
            />
            <button className="btn btn-primary btn-icon" style={{ width: 36, height: 36, padding: 0 }} onClick={handleEstSubmit}>
              <Check size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
